/**
 * viewerActions - Shared shortcut/action registry
 *
 * Single source of truth for all keyboard shortcut groups and items.
 * Used by HelpDialog, ViewportContextMenu, and ViewerToolbar to keep labels
 * in sync and to gate visibility by the active viewer surface.
 *
 * Each group declares the surfaces it belongs to. A group without `surfaces`
 * is universal (always rendered). When the active surface set is `null`
 * (page hasn't declared), nothing is filtered.
 */

export type Surface =
  | "dictation"
  | "labeling-conditions"
  | "labeling-seg"
  | "labeling-reporting"
  | "prediction-masks";

export const PREDICTION_MASK_SHORTCUT = "Shift+H";

export interface ShortcutItem {
  /** Display string for the keyboard shortcut (e.g. "Shift + S") */
  key: string;
  /** Human-readable description of what the shortcut does */
  description: string;
  /** If present, the item is actionable in the context menu */
  actionId?: string;
  /** Restrict this individual item to a subset of the group's surfaces. */
  surfaces?: Surface[];
}

export interface ShortcutGroup {
  category: string;
  /** Section index — adjacent groups with different sections get a divider
   *  between them in the context menu. */
  section: number;
  /** Surfaces this group belongs to. Omitted = universal. */
  surfaces?: Surface[];
  items: ShortcutItem[];
}

export const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    category: "Viewer Controls",
    section: 0,
    items: [
      { key: "↑ / ↓", description: "Scroll through slices", actionId: "scroll" },
      {
        key: "W / H",
        description: "Window/Level (HU) tool — default mode",
        actionId: "tool_wl",
      },
      { key: "P", description: "Pan tool", actionId: "tool_pan" },
      { key: "Z", description: "Zoom tool", actionId: "tool_zoom" },
      { key: "R", description: "Reset view", actionId: "reset_view" },
      { key: "I", description: "Invert grayscale", actionId: "invert" },
      { key: "X", description: "Toggle reference lines", actionId: "toggle_reflines" },
      {
        key: PREDICTION_MASK_SHORTCUT,
        description: "Toggle AI prediction masks",
        surfaces: ["prediction-masks"],
      },
    ],
  },
  {
    category: "Case Navigation",
    section: 0,
    items: [
      { key: "←", description: "Previous case", actionId: "prev_case" },
      { key: "→", description: "Next case", actionId: "next_case" },
      {
        key: "N",
        description: "Skip to next unlabeled case",
        actionId: "next_unlabeled",
        // Only labeling pages wire onNextUnlabeled into useViewerHotkeys —
        // dictation has no unlabeled-skip target, so hide the entry there.
        surfaces: ["labeling-conditions", "labeling-seg", "labeling-reporting"],
      },
    ],
  },
  {
    category: "Labeling",
    section: 0,
    surfaces: ["labeling-conditions"],
    items: [
      { key: "↑ / K", description: "Move focus up through conditions" },
      { key: "↓ / J", description: "Move focus down through conditions" },
      { key: "P", description: "Mark focused condition as Present" },
      { key: "A", description: "Mark focused condition as Absent" },
    ],
  },
  {
    category: "Measurement Tools",
    section: 1,
    // Measurement is needed across all labeling and dictation surfaces.
    // Several OT-v1 conditions are size-gated (large AAA ≥5cm, ≥25% pleural
    // effusion, measurable soft-tissue hematoma), so condition labelers
    // need ruler/ROI to confirm thresholds.
    surfaces: ["dictation", "labeling-conditions", "labeling-seg", "labeling-reporting"],
    items: [
      {
        key: "L / Shift+U",
        description: "Toggle Ruler (length measurement)",
        actionId: "tool_ruler",
      },
      { key: "M / Shift+M", description: "Toggle Rectangle ROI", actionId: "tool_roi" },
      { key: "Delete", description: "Delete selected measurement", actionId: "delete" },
      {
        key: "Shift+Delete",
        description: "Clear measurements on this slice",
        actionId: "clear_slice_measurements",
      },
      {
        key: "Ctrl/⌘+Shift+Backspace",
        description: "Clear all measurements",
        actionId: "clear_all_measurements",
      },
      { key: "Esc", description: "Return to default mode", actionId: "escape" },
    ],
  },
  {
    category: "Segmentation Tools",
    section: 1,
    surfaces: ["labeling-seg"],
    items: [
      {
        key: "Shift + S",
        description: "Toggle Segmentation lasso (freehand contour)",
        actionId: "tool_lasso",
      },
      {
        key: "Shift + E",
        description: "Toggle Bump refinement tool (push/pull boundary)",
        actionId: "tool_bump",
      },
      { key: "[ / ]", description: "Decrease / Increase bump brush radius" },
      {
        key: "Shift + G",
        description: "Toggle Region Grow tool (one-click flood fill)",
        actionId: "tool_regiongrow",
      },
      {
        key: "Shift + I",
        description: "Toggle segmentation engine (AI Segmentation ↔ Threshold)",
        actionId: "tool_seg_engine",
      },
      { key: "Ctrl + Z", description: "Undo last contour or region grow click", actionId: "undo" },
      { key: "Esc", description: "Exit segmentation mode", actionId: "escape" },
      {
        key: "Delete / Backspace",
        description: "Clear current slice contours for active segment",
        actionId: "delete",
      },
    ],
  },
  {
    category: "Region Grow Refinement",
    section: 1,
    surfaces: ["labeling-seg"],
    items: [
      { key: "Shift + Click", description: "Add positive refinement point (include region)" },
      { key: "Ctrl + Click", description: "Add negative refinement point (exclude region)" },
      { key: "Ctrl + Z", description: "Undo last refinement click", actionId: "undo" },
      { key: "Esc", description: "Reject segmentation and clear all points", actionId: "escape" },
    ],
  },
  {
    category: "Window Presets",
    section: 2,
    items: [
      {
        key: "Shift+1 / Shift+T",
        description: "Soft Tissue (W:400 L:40)",
        actionId: "preset_soft_tissue",
      },
      { key: "Shift+2 / Shift+L", description: "Lung (W:1500 L:-600)", actionId: "preset_lung" },
      {
        key: "Shift+3 / Shift+B / B",
        description: "Bone (W:1800 L:400)",
        actionId: "preset_bone",
      },
      { key: "Shift+4 / Shift+R", description: "Brain (W:80 L:40)", actionId: "preset_brain" },
      { key: "Shift+5 / Shift+V", description: "Liver (W:150 L:60)", actionId: "preset_liver" },
    ],
  },
  {
    category: "Layout",
    section: 3,
    items: [
      { key: ",", description: "Toggle left sidebar", actionId: "toggle_left_sidebar" },
      { key: ".", description: "Toggle right sidebar", actionId: "toggle_right_sidebar" },
      { key: "V", description: "Cycle active viewport" },
    ],
  },
  {
    category: "Report Editor",
    section: 3,
    surfaces: ["dictation", "labeling-reporting"],
    items: [
      { key: "Tab / Shift+Tab", description: "Navigate between merge fields" },
      { key: "Ctrl + N", description: "Toggle normal fields" },
      { key: "Ctrl + G", description: "Generate / re-generate impression" },
      { key: "` (hold)", description: "Dictate at the cursor (release to transcribe)" },
      { key: "Right-click", description: "Finding / section options menu" },
      { key: "Ctrl / ⌘ + E", description: "Focus report editor" },
      { key: "↑ / ↓", description: "Navigate To Consider items" },
      { key: "Y / Enter", description: "Accept focused To Consider finding" },
      { key: "Backspace / Delete", description: "Dismiss focused To Consider finding" },
    ],
  },
  {
    category: "Mouse Controls",
    section: 4,
    items: [
      { key: "Scroll wheel", description: "Scroll through slices" },
      { key: "Left click + drag", description: "Adjust window/level" },
      { key: "Middle click + drag", description: "Pan image" },
      { key: "Right click + drag", description: "Zoom in/out" },
      { key: "Double click", description: "Toggle spotlight on viewport" },
    ],
  },
];

/** Filter groups (and per-item surface overrides) by the active surface set.
 *  `active === null` short-circuits — nothing is filtered. */
export function filterShortcutGroupsBySurfaces(
  groups: ShortcutGroup[],
  active: Surface[] | null,
): ShortcutGroup[] {
  if (!active) return groups;
  const matches = (s: Surface[] | undefined) => !s || s.some((x) => active.includes(x));
  return groups
    .filter((g) => matches(g.surfaces))
    .map((g) => {
      const items = g.items.filter((i) => matches(i.surfaces));
      return items.length === g.items.length ? g : { ...g, items };
    })
    .filter((g) => g.items.length > 0);
}
